import { isVenueProjection } from './venue-responses.js';
import { parseOccurrenceTimingWrite } from '@liquid-spirit/date-time/validation';
import { isCalendarTimingProjection } from './timing-projections.js';
import {readOccurrenceReadContext} from './occurrence-read-contract.js';
/** A server response violated an endpoint's documented wire contract. */
export class EventResponseContractError extends Error {
  constructor(operation, detail) {
    super(`Invalid ${operation} response: ${detail}`);
    this.name = 'EventResponseContractError';
    this.code = 'INVALID_API_RESPONSE';
  }
}

const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const hasId = value => typeof value === 'string' && value.length > 0;
const isReference = value => hasId(value) || (isRecord(value) && hasId(value._id));
const entityTypes = new Set(['User', 'Member', 'Guest']);
const venueRequestStatuses = new Set(['pending', 'approved', 'declined', 'cancelled', 'superseded']);
const venueTypes = new Set(['Residence', 'Online', 'CommunityVenue']);
const bodyTypes = new Set(['Feast Committee', 'Holy Days Committee', 'Local Spiritual Assembly']);
const optionalText = value => value === undefined || value === null || typeof value === 'string';
const optionalReference = value => value === undefined || value === null || isReference(value);
const optionalDate = value => value === undefined || value === null
  || (typeof value === 'string' || value instanceof Date) && Number.isFinite(Date.parse(value));
const secretFree = value => !['password', 'refreshToken', 'resetPasswordToken', 'resetPasswordExpires', 'emailVerificationCode', 'emailVerificationExpires'].some(field => Object.hasOwn(value, field))
  && value.verificationCode == null;

const isEntityDetails = details => details === null || isRecord(details)
  && secretFree(details)
  && (details._id === undefined || hasId(details._id))
  && ['firstName', 'lastName', 'email', 'bahaiId', 'profilePicture', 'referenceContact'].every(field => optionalText(details[field])
    || field === 'referenceContact' && isReference(details[field])
      && (!isRecord(details[field]) || isEntityDetails(details[field])));
const isCertifications = certifications => certifications == null || isRecord(certifications)
  && ['isVerified', 'hasChildProtection', 'isLocalAssemblyMember'].every(field => certifications[field] === undefined || typeof certifications[field] === 'boolean')
  && (certifications.ruhiBadges === undefined || Array.isArray(certifications.ruhiBadges)
    && certifications.ruhiBadges.every(badge => typeof badge === 'string'));
const isVenueSummary = venue => isVenueProjection(venue)
  && typeof venue.name === 'string' && venue.name.length > 0
  && (venue.type === undefined || venueTypes.has(venue.type))
  && optionalReference(venue.community) && optionalReference(venue.household)
  && optionalText(venue.onlineLink)
  && (venue.availableForRequest === undefined || typeof venue.availableForRequest === 'boolean')
  && (venue.address == null || isRecord(venue.address)
    && ['streetAddress', 'suburb', 'city', 'state', 'postalCode'].every(field => optionalText(venue.address[field])));
const isOversightMember = member => isRecord(member) && secretFree(member) && hasId(member._id) && hasId(member.id)
  && ['User', 'Member'].includes(member.type)
  && ['User', 'Member'].includes(member.userModel)
  && ['firstName', 'lastName', 'fullName'].every(field => typeof member[field] === 'string')
  && optionalText(member.email) && optionalText(member.bahaiId)
  && (member.role === null || ['Admin', 'Member'].includes(member.role))
  && typeof member.isAdmin === 'boolean' && optionalReference(member.membershipId);

const readEntityList = (items, operation, field) => {
  if (!Array.isArray(items) || items.some(item => !isRecord(item)
    || !entityTypes.has(item.type) || !isReference(item.refId)
    || isRecord(item.refId) && !isEntityDetails(item.refId)
    || !['firstName', 'lastName', 'email', 'profilePicture'].every(field => optionalText(item[field]))
    || (item.details !== undefined && !isEntityDetails(item.details))
    || (item.certifications !== undefined && !isCertifications(item.certifications)))) {
    throw new EventResponseContractError(operation, `expected ${field}[] with typed entity references.`);
  }
};

const readMaterialList = (items, operation) => {
  if (!Array.isArray(items) || items.some(item => !isRecord(item)
    || (item._id !== undefined && item._id !== null && !hasId(item._id))
    || (item.filename !== undefined && typeof item.filename !== 'string')
    || (item.url !== undefined && typeof item.url !== 'string')
    || (item.fileType !== undefined && item.fileType !== null && typeof item.fileType !== 'string'))) {
    throw new EventResponseContractError(operation, 'expected materials[] with valid file fields.');
  }
};

const readEventRelationships = (event, operation) => {
  const timingFields = ['timingVersion', 'timingRevision', 'timeZone', 'startsAt', 'endsAt', 'localStart', 'localEnd'];
  if (event.calendarTiming != null) {
    if (!isCalendarTimingProjection(event) || event.startTime !== null || event.endTime !== null) {
      throw new EventResponseContractError(operation, 'expected calendar timing without scheduled instants.');
    }
  } else if (timingFields.some(field => event[field] !== undefined)) {
    try {
      parseOccurrenceTimingWrite(event);
      if (event.timingVersion !== 1 || !Number.isSafeInteger(event.timingRevision) || event.timingRevision < 1
        || timingFields.some(field => event[field] === undefined) || !event.localStart
        || (event.endsAt === null) !== (event.localEnd === null)
        || event.endsAt !== null && new Date(event.endsAt) <= new Date(event.startsAt)
        || !optionalDate(event.startTime) || event.startTime == null || new Date(event.startTime).getTime() !== new Date(event.startsAt).getTime()
        || (event.endTime == null ? null : new Date(event.endTime).getTime()) !== (event.endsAt === null ? null : new Date(event.endsAt).getTime())
        || !optionalDate(event.date) || event.date == null || new Date(event.date).toISOString().slice(0, 10) !== event.localStart.date) {
        throw new Error('incomplete or conflicting canonical fields');
      }
    } catch { throw new EventResponseContractError(operation, 'expected complete canonical timing with agreeing legacy mirrors.'); }
  }
  if (!['title', 'description', 'venue', 'imageUrl'].every(field => optionalText(event[field]))
    || (event.eventType !== undefined && !['Feast', 'Holy Day', 'Admin', 'Community'].includes(event.eventType))
    || !['date', 'startTime', 'endTime', 'bookingEndsAt'].every(field => optionalDate(event[field]))
    || !optionalReference(event.community)
    || (isRecord(event.community) && !optionalText(event.community.name))
    || (event.isOpenExternally !== undefined && typeof event.isOpenExternally !== 'boolean')) {
    throw new EventResponseContractError(operation, 'expected valid event text, date and community fields.');
  }
  if (event.createdBy !== undefined && event.createdBy !== null && (!isReference(event.createdBy)
    || (isRecord(event.createdBy) && (!secretFree(event.createdBy) || 'password' in event.createdBy
      || 'bahaiId' in event.createdBy || 'refreshToken' in event.createdBy
      || !['firstName', 'lastName', 'profilePicture'].every(field => optionalText(event.createdBy[field])))))) {
    throw new EventResponseContractError(operation, 'expected a public creator reference.');
  }
  for (const field of ['attendees', 'hosts', 'hostRequests']) {
    if (event[field] !== undefined) readEntityList(event[field], operation, field);
  }
  if (event.materials !== undefined) readMaterialList(event.materials, operation);
  if (event.venues !== undefined && (!Array.isArray(event.venues)
    || event.venues.some(venue => !isVenueSummary(venue)))) {
    throw new EventResponseContractError(operation, 'expected populated venues[] with identity and name.');
  }
  if (event.venueRequests !== undefined && (!Array.isArray(event.venueRequests)
    || event.venueRequests.some(request => !(
      hasId(request) || (isRecord(request) && hasId(request._id)
        && venueRequestStatuses.has(request.status)
        && (request.targetVenue === undefined || request.targetVenue === null
          || (isRecord(request.targetVenue) ? isVenueSummary(request.targetVenue) : isReference(request.targetVenue)))
        && (request.kind === undefined || ['residence', 'communityVenueBooking'].includes(request.kind))
        && (request.approvalMode === undefined || request.approvalMode === null || ['once', 'ongoing'].includes(request.approvalMode))
        && (request.requiresAssemblyApproval === undefined || typeof request.requiresAssemblyApproval === 'boolean')
        && optionalReference(request.requestedByUser) && optionalReference(request.approverUser)
        && optionalReference(request.decidedByUser) && optionalReference(request.targetHousehold)
        && optionalReference(request.activity) && optionalDate(request.decidedAt) && optionalDate(request.createdAt)))))) {
    throw new EventResponseContractError(operation, 'expected venueRequests[] with references or status records.');
  }
  if (event.oversight !== undefined && (!isRecord(event.oversight)
    || typeof event.oversight.applies !== 'boolean'
    || typeof event.oversight.bodyType !== 'string'
    || typeof event.oversight.bodyLabel !== 'string'
    || (event.oversight.bodyId !== null && !hasId(event.oversight.bodyId))
    || typeof event.oversight.bodyName !== 'string'
    || !Array.isArray(event.oversight.members)
    || event.oversight.members.some(member => !isOversightMember(member))
    || (event.oversight.applies && !bodyTypes.has(event.oversight.bodyType))
    || (!event.oversight.applies && event.oversight.bodyType !== ''))) {
    throw new EventResponseContractError(operation, 'expected a complete event oversight summary.');
  }
  if (event.userBodyMembership !== undefined && event.userBodyMembership !== null
    && typeof event.userBodyMembership !== 'boolean'
    && (!isRecord(event.userBodyMembership) || Object.entries(event.userBodyMembership).some(([type, member]) => !bodyTypes.has(type) || typeof member !== 'boolean'))) {
    throw new EventResponseContractError(operation, 'expected typed body membership decisions.');
  }
};

/**
 * Both event-list endpoints return { success: true, data: EventSummary[] }.
 * Validate at the service boundary: malformed data is not an empty community.
 * Keep the existing wire ID (_id) until the versioned identity migration.
 */
export function readEventListResponse(payload, operation = 'event list') {
  if (!payload || payload.success !== true || !Array.isArray(payload.data)) {
    throw new EventResponseContractError(operation, 'expected { success: true, data: EventSummary[] }.');
  }
  readOccurrenceReadContext(payload);
  if (payload.data.some(event => !isRecord(event) || !hasId(event._id))) {
    throw new EventResponseContractError(operation, 'every event must have a non-empty string _id.');
  }
  payload.data.forEach(event => readEventRelationships(event, operation));
  return payload.data;
}


/** Event detail endpoints return the entity directly. */
export function readEventDetailResponse(payload, operation = 'event detail') {
  if (!isRecord(payload) || !hasId(payload._id)) {
    throw new EventResponseContractError(operation, 'expected an event with a non-empty string _id.');
  }
  readEventRelationships(payload, operation);
  return payload;
}

/** Creation and field updates return { event, message }; callers receive Event. */
export function readEventMutationResponse(payload) {
  return readEventDetailResponse(payload?.event, 'event mutation');
}

/** Image and attendance writes return an event inside a message envelope. */
export function readEventWrappedResponse(payload, operation = 'event action') {
  if (!payload || typeof payload.message !== 'string' || !payload.message) {
    throw new EventResponseContractError(operation, 'expected { message, event }.');
  }
  readEventDetailResponse(payload.event, operation);
  return payload;
}

/** Deletion acknowledges the removed resource. */
export function readEventMessageResponse(payload, operation = 'event deletion') {
  if (!payload || typeof payload.message !== 'string' || !payload.message) {
    throw new EventResponseContractError(operation, 'expected { message: string }.');
  }
  return payload;
}

/** Host-request submit/decline return the remaining request list. */
export function readEventHostRequestsResponse(payload, operation = 'event host requests') {
  readEventMessageResponse(payload, operation);
  readEntityList(payload.hostRequests, operation, 'hostRequests');
  return payload;
}

/** Accept additionally returns the resulting hosts list. */
export function readEventHostAcceptanceResponse(payload) {
  readEventHostRequestsResponse(payload, 'event host acceptance');
  readEntityList(payload.hosts, 'event host acceptance', 'hosts');
  return payload;
}

/** Removing a material returns the remaining materials. */
export function readEventMaterialsResponse(payload) {
  readEventMessageResponse(payload, 'event material removal');
  readMaterialList(payload.materials, 'event material removal');
  return payload;
}
